import { useState } from 'react';
import { useOptionallyControlledPropGuard } from './useOptionallyControlledPropGuard';

/**
 * Wrap useState hook with possibility to override it with value and onChange passed from outside
 * to create optionally controlled components
 *
 * To get idea what optionally controlled components are, see
 * https://medium.com/quick-code/writing-ui-components-with-optionally-controllable-state-86e396a6f1ec
 * We have slightly different implementation, especially we aren't updating local state when
 * a value is passed from outside to avoid unnecessary renders, but the principle is the same.
 */
export function useOptionallyControlledState<S>(
  value: S | undefined,
  onChange: ((newValue: S) => void) | undefined,
  initialValue: S | (() => S),
): [S, (newValue: S) => void];
// no initialValue argument variant when S allows undefined
export function useOptionallyControlledState<S = undefined>(
  value: S | undefined,
  onChange: ((newValue: S) => void) | undefined,
): [S, (newValue: S) => void];
export function useOptionallyControlledState<S>(
  value: S | undefined,
  onChange: ((newValue: S) => void) | undefined,
  initialValue?: S | (() => S),
): [S, (newValue: S) => void] {
  useOptionallyControlledPropGuard(value);
  const [stateValue, setState] = useState(initialValue);
  const effectiveValue = value !== undefined ? value : stateValue;
  return [
    effectiveValue as S, // thanks to overload declarations it can be undefined only when S allows undefined
    (newValue) => {
      if (value === undefined) {
        // update local state only in uncontrolled mode to avoid unnecessary re-renders in controlled mode
        setState(newValue);
      }
      onChange?.(newValue);
    },
  ];
}
